import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import './Login.css'
import api from '../config/api'

const PasswordReset = () => {
    const [passwords, setPasswords] = useState({
        new_password: '',
        confirm_password: '',
    })
    const [loading, setLoading] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [error, setError] = useState('')

    const { uid, token } = useParams()
    const navigate = useNavigate()

    const handleChange = (e) => {
        setPasswords({
            ...passwords,
            [e.target.name]: e.target.value,
        })
        if (error) setError('')
    }

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword((prev) => !prev)
    }

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword((prev) => !prev)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')

        if (passwords.new_password !== passwords.confirm_password) {
            const msg = 'The passwords you entered do not match. Please try again.'
            setError(msg)
            Swal.fire({
                icon: 'error',
                title: 'Password Mismatch',
                text: msg,
                confirmButtonColor: '#4F46E5',
            })
            return
        }

        // Password validation
        if (passwords.new_password.length < 8) {
            const msg = 'Password must be at least 8 characters long.'
            setError(msg)
            Swal.fire({
                icon: 'error',
                title: 'Invalid Password',
                text: msg,
                confirmButtonColor: '#4F46E5',
            })
            return
        }

        setLoading(true)

        try {
            await api.post('/api/reset-password/', {
                token: token,
                ...(uid ? { uid } : {}),
                new_password: passwords.new_password,
                confirm_password: passwords.confirm_password,
            })

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Your password has been reset successfully.',
                timer: 2000,
                showConfirmButton: false,
                confirmButtonColor: '#4F46E5',
                willClose: () => {
                    navigate('/login')
                },
            })
            navigate('/login')
        } catch (err) {
            console.error('Password reset error:', err)

            let errorMessage = 'Something went wrong. Please try again.'

            if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail
            } else if (err.response?.data?.non_field_errors) {
                errorMessage = err.response.data.non_field_errors[0]
            }

            setError(errorMessage)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
                confirmButtonColor: '#4F46E5',
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="login-page">
            <div className="background-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-4"></div>
                <div className="shape shape-5"></div>
                <div className="shape shape-6"></div>
                <div className="shape shape-7"></div>
                <div className="shape shape-8"></div>
                <div className="shape shape-9"></div>
            </div>

            <div className="login-container">
                <div className="logo-container mb-4">
                    <img
                        src="assets/img/media/celebration-logo.png"
                        alt="Company Logo"
                        className="company-logo"
                        onError={(e) => {
                            e.target.style.display = 'none'
                            const logoText = document.querySelector('.logo-text')
                            if (logoText) logoText.style.display = 'block'
                        }}
                    />
                    <h2 className="logo-text" style={{ display: 'none' }}>
                        PMS
                    </h2>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <h4 className="fw-bold text-white text-center mb-4">Reset Password</h4>

                    {error && (
                        <div
                            className="alert alert-danger border-0 mb-4 shadow-sm fs-14"
                            role="alert"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <i className="ti ti-alert-triangle fs-18 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}

                    {/* New Password */}
                    <div className="form-group mb-3">
                        <label
                            htmlFor="new_password"
                            className="form-label fs-13 fw-medium mb-2"
                            style={{ color: 'rgba(255,255,255,0.85)' }}
                        >
                            New Password
                        </label>
                        <div className="input-container position-relative">
                            <span
                                className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center"
                                style={{ left: '14px', color: 'rgba(255,255,255,0.55)' }}
                            >
                                <i className="ti ti-lock fs-18" />
                            </span>
                            <input
                                type={showNewPassword ? 'text' : 'password'}
                                id="new_password"
                                name="new_password"
                                className="form-control form-control-lg fs-14"
                                style={{
                                    paddingLeft: '46px',
                                    paddingRight: '48px',
                                    backgroundColor: 'rgba(255,255,255,0.07)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    color: '#fff',
                                    borderRadius: '10px',
                                }}
                                value={passwords.new_password}
                                onChange={handleChange}
                                placeholder="Enter new password"
                                autoComplete="new-password"
                                required
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-decoration-none p-0 me-3 d-flex align-items-center justify-content-center"
                                onClick={toggleNewPasswordVisibility}
                                style={{ color: 'rgba(255,255,255,0.6)' }}
                                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                            >
                                <i
                                    className={`ti ${
                                        showNewPassword ? 'ti-eye-off' : 'ti-eye'
                                    } fs-18`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="form-group mb-3" style={{ marginTop: '20px' }}>
                        <label
                            htmlFor="confirm_password"
                            className="form-label fs-13 fw-medium mb-2"
                            style={{ color: 'rgba(255,255,255,0.85)' }}
                        >
                            Confirm Password
                        </label>
                        <div className="input-container position-relative">
                            <span
                                className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center"
                                style={{ left: '14px', color: 'rgba(255,255,255,0.55)' }}
                            >
                                <i className="ti ti-lock fs-18" />
                            </span>
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirm_password"
                                name="confirm_password"
                                className="form-control form-control-lg fs-14"
                                style={{
                                    paddingLeft: '46px',
                                    paddingRight: '48px',
                                    backgroundColor: 'rgba(255,255,255,0.07)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    color: '#fff',
                                    borderRadius: '10px',
                                }}
                                value={passwords.confirm_password}
                                onChange={handleChange}
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                                required
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-decoration-none p-0 me-3 d-flex align-items-center justify-content-center"
                                onClick={toggleConfirmPasswordVisibility}
                                style={{ color: 'rgba(255,255,255,0.6)' }}
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            >
                                <i
                                    className={`ti ${
                                        showConfirmPassword ? 'ti-eye-off' : 'ti-eye'
                                    } fs-18`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="btn w-100 py-3 fw-bold fs-15 shadow-lg"
                        disabled={loading}
                        style={{
                            marginTop: '25px',
                            background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                            border: 'none',
                            color: '#fff',
                            borderRadius: '10px',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        }}
                        onMouseOver={(e) => {
                            if (!loading) {
                                e.currentTarget.style.transform = 'translateY(-1px)'
                                e.currentTarget.style.boxShadow =
                                    '0 10px 24px rgba(79, 70, 229, 0.35)'
                            }
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow =
                                '0 8px 16px rgba(0, 0, 0, 0.15)'
                        }}
                    >
                        {loading ? (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                                <span
                                    className="spinner-border spinner-border-sm text-white"
                                    role="status"
                                    aria-hidden="true"
                                />
                                <span>Resetting Password...</span>
                            </span>
                        ) : (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                                <i className="ti ti-key fs-18" />
                                Reset Password
                            </span>
                        )}
                    </button>

                    <div className="text-center pt-3">
                        <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none fs-13 text-light opacity-75"
                            onClick={() => navigate('/login')}
                        >
                            <i className="ti ti-arrow-left me-1" />
                            Back to Login
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default PasswordReset